/**
 * Context menu on mode cards — Pin, Description (PC right-click, mobile long-press).
 */

import { getModeDetailTitle, getModeFullDescription } from "./mode-details";
import { isModePinned, pinMode, unpinMode } from "./mode-pins";
import { refreshModesScreen } from "./modes-screen";

let activeModeId: string | null = null;
let longPressTimer: ReturnType<typeof setTimeout> | null = null;
let longPressStart = { x: 0, y: 0 };
let mounted = false;

const LONG_PRESS_MS = 480;
const MOVE_CANCEL_PX = 12;

function menuEl() {
    return document.getElementById("modeContextMenu");
}

function detailModal() {
    return document.getElementById("modeDetailModal");
}

function hideMenu() {
    menuEl()?.classList.add("hidden");
    activeModeId = null;
}

function syncPinMenuItems(modeId: string) {
    const pinned = isModePinned(modeId);
    document.getElementById("modeCtxPinBtn")?.classList.toggle("hidden", pinned);
    document.getElementById("modeCtxUnpinBtn")?.classList.toggle("hidden", !pinned);
}

function syncRankingMenuItem(modeId: string) {
    const hasRanking = modeId !== "multiplayer";
    document.getElementById("modeCtxRankingBtn")?.classList.toggle("hidden", !hasRanking);
}

function openSeeInRanking(modeId: string) {
    hideMenu();
    document.dispatchEvent(new CustomEvent("cursorgame:open-rankings-mode", { detail: { modeId } }));
}

function openDescription(modeId: string) {
    hideMenu();
    const modal = detailModal();
    const title = document.getElementById("modeDetailTitle");
    const body = document.getElementById("modeDetailBody");
    if (!modal || !title || !body) return;
    title.textContent = getModeDetailTitle(modeId);
    body.innerHTML = getModeFullDescription(modeId);
    modal.classList.remove("hidden");
}

function closeDescription() {
    detailModal()?.classList.add("hidden");
}

function showMenu(modeId: string, clientX: number, clientY: number, anchor?: HTMLElement) {
    const menu = menuEl();
    if (!menu) return;
    activeModeId = modeId;
    syncPinMenuItems(modeId);
    syncRankingMenuItem(modeId);
    menu.classList.remove("hidden");
    const pad = 8;
    let x = clientX;
    let y = clientY;
    if (anchor) {
        const rect = anchor.getBoundingClientRect();
        x = rect.left;
        y = rect.bottom + 6;
    }
    const rect = menu.getBoundingClientRect();
    if (x + rect.width > window.innerWidth - pad) x = window.innerWidth - rect.width - pad;
    if (y + rect.height > window.innerHeight - pad) y = window.innerHeight - rect.height - pad;
    menu.style.left = `${Math.max(pad, x)}px`;
    menu.style.top = `${Math.max(pad, y)}px`;
}

function ensureModeMenuBtn(box: HTMLElement) {
    if (box.querySelector(".modeBoxMenuBtn")) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "modeBoxMenuBtn";
    btn.setAttribute("aria-label", "Mode options");
    btn.setAttribute("aria-haspopup", "menu");
    btn.textContent = "...";
    box.prepend(btn);
}

export function syncModeCardMenuButtons() {
    document.querySelectorAll("#modesContainer .modeBox[data-mode-id]").forEach((box) => {
        if (box instanceof HTMLElement) ensureModeMenuBtn(box);
    });
}

function modeIdFromTarget(target: EventTarget | null): string | null {
    if (!(target instanceof Element)) return null;
    const box = target.closest(".modeBox[data-mode-id]");
    if (!box || box.closest("#modesContainer") == null) return null;
    return box.getAttribute("data-mode-id");
}

function onMenuAction(action: "pin" | "unpin" | "description" | "ranking") {
    const id = activeModeId;
    hideMenu();
    if (!id) return;
    if (action === "pin") {
        pinMode(id);
        refreshModesScreen();
        return;
    }
    if (action === "unpin") {
        unpinMode(id);
        refreshModesScreen();
        return;
    }
    if (action === "ranking") {
        openSeeInRanking(id);
        return;
    }
    openDescription(id);
}

function bindModeGridEvents(grid: HTMLElement) {
    grid.addEventListener("click", (e) => {
        if (!(e.target instanceof Element)) return;
        const menuBtn = e.target.closest(".modeBoxMenuBtn");
        if (!menuBtn) return;
        e.preventDefault();
        e.stopPropagation();
        const box = menuBtn.closest(".modeBox[data-mode-id]");
        const modeId = box?.getAttribute("data-mode-id");
        if (!modeId || !(menuBtn instanceof HTMLElement)) return;
        showMenu(modeId, 0, 0, menuBtn);
    });

    grid.addEventListener("contextmenu", (e) => {
        const modeId = modeIdFromTarget(e.target);
        if (!modeId) return;
        e.preventDefault();
        showMenu(modeId, e.clientX, e.clientY);
    });

    grid.addEventListener(
        "pointerdown",
        (e) => {
            if (!(e.target instanceof Element)) return;
            if (e.target.closest(".modeBoxMenuBtn")) return;
            const modeId = modeIdFromTarget(e.target);
            if (!modeId || e.pointerType === "mouse") return;
            longPressStart = { x: e.clientX, y: e.clientY };
            if (longPressTimer) clearTimeout(longPressTimer);
            longPressTimer = setTimeout(() => {
                longPressTimer = null;
                showMenu(modeId, e.clientX, e.clientY);
            }, LONG_PRESS_MS);
        },
        { passive: true },
    );

    grid.addEventListener(
        "pointermove",
        (e) => {
            if (!longPressTimer) return;
            if (Math.hypot(e.clientX - longPressStart.x, e.clientY - longPressStart.y) > MOVE_CANCEL_PX) {
                clearTimeout(longPressTimer);
                longPressTimer = null;
            }
        },
        { passive: true },
    );

    grid.addEventListener(
        "pointerup",
        () => {
            if (longPressTimer) {
                clearTimeout(longPressTimer);
                longPressTimer = null;
            }
        },
        { passive: true },
    );

    grid.addEventListener(
        "pointercancel",
        () => {
            if (longPressTimer) {
                clearTimeout(longPressTimer);
                longPressTimer = null;
            }
        },
        { passive: true },
    );
}

export function mountModeContextMenu() {
    if (mounted) return;
    mounted = true;

    document.getElementById("modeCtxPinBtn")?.addEventListener("click", () => onMenuAction("pin"));
    document.getElementById("modeCtxUnpinBtn")?.addEventListener("click", () => onMenuAction("unpin"));
    document.getElementById("modeCtxDescriptionBtn")?.addEventListener("click", () => onMenuAction("description"));
    document.getElementById("modeCtxRankingBtn")?.addEventListener("click", () => onMenuAction("ranking"));
    document.getElementById("modeCtxCloseBtn")?.addEventListener("click", (e) => {
        e.stopPropagation();
        hideMenu();
    });
    document.getElementById("modeDetailCloseBtn")?.addEventListener("click", closeDescription);

    detailModal()?.addEventListener("click", (e) => {
        if (e.target === detailModal()) closeDescription();
    });

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
            hideMenu();
            closeDescription();
        }
    });

    document.addEventListener(
        "click",
        (e) => {
            if (!(e.target instanceof Element)) return;
            if (e.target.closest("#modeContextMenu") || e.target.closest(".modeBoxMenuBtn")) return;
            hideMenu();
        },
        true,
    );

    for (const id of ["modesPinnedGrid", "modesSpGrid"]) {
        const grid = document.getElementById(id);
        if (grid) bindModeGridEvents(grid);
    }

    syncModeCardMenuButtons();
}

export function closeModeModals() {
    hideMenu();
    closeDescription();
}
